import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LeadStatus } from '../../types';
import {
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  leadStatusClassName,
} from './leadStatusDisplay';

export type LeadStatusPillVariant = 'table' | 'header';

interface LeadStatusPillProps {
  status: LeadStatus;
  onChange?: (status: LeadStatus) => void;
  readonly?: boolean;
  variant?: LeadStatusPillVariant;
  onOpenChange?: (open: boolean) => void;
}

type MenuPos = { top?: number; bottom?: number; left?: number; right?: number };

const LeadStatusPill: React.FC<LeadStatusPillProps> = ({
  status,
  onChange,
  readonly,
  variant = 'table',
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const editable = !readonly && !!onChange;
  const pillClass =
    variant === 'header'
      ? `cw-status-badge ${leadStatusClassName(status)}`
      : `leads-status-pill ${leadStatusClassName(status)}`;

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useLayoutEffect(() => {
    if (!open || !ref.current) {
      setMenuPos(null);
      return;
    }
    const place = () => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 160;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 220 && rect.top > spaceBelow;
      const alignRight = rect.right > window.innerWidth - menuWidth - 12;
      const next: MenuPos = alignRight
        ? { right: Math.max(8, window.innerWidth - rect.right) }
        : { left: Math.max(8, rect.left) };
      if (openUp) next.bottom = window.innerHeight - rect.top + 4;
      else next.top = rect.bottom + 4;
      setMenuPos(next);
    };
    place();
    window.addEventListener('resize', place);
    document.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const menu =
    open &&
    menuPos &&
    createPortal(
      <div
        ref={menuRef}
        className="pm-dropdown leads-status-menu pm-status-dropdown--portal"
        role="listbox"
        aria-label="Lead status"
        style={menuPos}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        {LEAD_STATUSES.map(s => (
          <button
            key={s}
            type="button"
            role="option"
            aria-selected={s === status}
            className="pm-dropdown-item leads-status-menu-item"
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
              onChange?.(s);
              setOpen(false);
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <span className={`leads-status-pill ${leadStatusClassName(s)}`}>
              {LEAD_STATUS_LABELS[s]}
            </span>
          </button>
        ))}
      </div>,
      document.body,
    );

  return (
    <div
      ref={ref}
      className="leads-status-pill-host"
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      <span
        className={`${pillClass}${editable ? ' leads-status-pill--editable' : ''}`}
        role={editable ? 'button' : undefined}
        tabIndex={editable ? 0 : undefined}
        aria-haspopup={editable ? 'listbox' : undefined}
        aria-expanded={editable ? open : undefined}
        title={editable ? 'Change lead status' : undefined}
        onClick={() => {
          if (!editable) return;
          setOpen(prev => !prev);
        }}
        onKeyDown={e => {
          if (!editable) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(prev => !prev);
          }
        }}
      >
        {LEAD_STATUS_LABELS[status] ?? status}
      </span>
      {menu}
    </div>
  );
};

export default LeadStatusPill;
